// MACHINERY V2 (Stroje) — fleet mutation handlers (M2.2 logic, extracted M2.3).
//
// Duplicate, Restore, and the per-machine kebab action set. Split out of the
// fleet page when M2.3 added the second view, so the page stays a composition
// and BOTH views get identical behaviour from one place — a row and a card must
// never do different things behind the same menu item.
//
// Nothing here is new logic: it is M2.2's handlers moved verbatim, including
// the two review fixes they carry (F5's `queryRef` merge base and F6's
// try/catch around the unwrapped restore).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { MutableRefObject, useCallback } from "react";
import { NavigateFunction } from "react-router-dom";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import { Machine, MachinesQuery } from "../../models/models";
import {
  createMachine,
  getDecommissionedMachines,
  getMachines,
  restoreMachine,
  setError,
  setSuccess,
} from "../../customerMachinerySlice";
import { FleetMenuAction } from "./FleetCardMenu";
import { buildFleetActions } from "./fleetActions";
import { buildDuplicatePayload } from "./fleetDuplicate";
import { FleetDialogControls } from "./useFleetDialogs";

export interface FleetHandlers {
  buildActions: (m: Machine) => FleetMenuAction[];
}

export const useFleetHandlers = (
  canWrite: boolean,
  queryRef: MutableRefObject<MachinesQuery>,
  dialogs: FleetDialogControls,
  navigate: NavigateFunction
): FleetHandlers => {
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const handleDuplicate = useCallback(
    async (m: Machine) => {
      // The v2 payload carries catalogModelId AND locationId — fleetDuplicate.ts.
      // Rows and cards share it, so the fix cannot regress in one view only.
      const payload = buildDuplicatePayload(m);
      if (!payload) {
        dispatch(setError(translate("myMachinery:v2.fleet.duplicateMissingFields")));
        return;
      }
      await dispatch(createMachine(payload));
      await dispatch(getMachines(queryRef.current));
    },
    [dispatch, queryRef, translate]
  );

  const handleRestore = useCallback(
    (m: Machine) => {
      // The EXISTING confirm dialog, reused — §9 D4 carries the action, not a
      // redesign of its dialog.
      confirm({
        title: translate("myMachinery:restoreMachine"),
        text: translate("myMachinery:restoreConfirm"),
        confirmButton: translate("myMachinery:restore"),
        // `common.json` has NO top-level `cancel` key — tCommon("cancel")
        // rendered the literal string "cancel" in BOTH locales. The real key
        // is buttons.cancel (en "Cancel" / sk "Zrušiť").
        closeButton: tCommon("buttons.cancel"),
        severity: "neutral",
        // REVIEW F6 — ConfirmationProvider neither awaits nor catches this
        // callback, so an unwrapped rejection became an unhandled promise
        // rejection AND skipped both refreshes. The live handler
        // (Machinery.tsx:676-681) wraps it; so do we. The slice has already
        // stored the error, and MachineryToastHost renders it.
        onConfirm: async () => {
          try {
            await dispatch(restoreMachine(m.id)).unwrap();
            dispatch(setSuccess(translate("myMachinery:restored")));
          } catch {
            // Error surfaced by the slice -> MachineryToastHost.
          }
          await dispatch(getMachines(queryRef.current));
          await dispatch(getDecommissionedMachines());
        },
      });
    },
    [confirm, dispatch, queryRef, translate, tCommon]
  );

  const buildActions = useCallback(
    (m: Machine) =>
      buildFleetActions(m, canWrite, {
        // Repointed at M2.4 to the v2 profile. Kept identical to FleetPage's
        // openProfile so the kebab entry and the card/row click cannot diverge.
        onProfile: (x) => navigate(`/machinery-v2/${x.id}`),
        onQrCode: dialogs.openQr,
        onCreateServiceReport: (x) => navigate(`/machinery/${x.id}/service-reports/new`),
        onCreateServiceRequest: (x) => navigate(`/service-requests/new?machine=${x.id}`),
        onEditDetails: dialogs.openEdit,
        onDuplicate: handleDuplicate,
        onAssignGroup: dialogs.openAssignGroup,
        onDecommission: dialogs.openDecommission,
        onRestore: handleRestore,
      }),
    [canWrite, dialogs, navigate, handleDuplicate, handleRestore]
  );

  return { buildActions };
};

export default useFleetHandlers;
